import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { PlusIcon, SearchIcon, ReceiptIcon, FilterIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { InvoiceCard, Invoice } from '../components/InvoiceCard';
import { AddInvoiceModal } from '../components/AddInvoiceModal';
import { EditInvoiceModal } from '../components/EditInvoiceModal';
interface InvoicesProps {
  invoices: Invoice[];
  onAddInvoice: (invoice: Omit<Invoice, 'id'>) => void;
  onEditInvoice: (invoice: Invoice) => void;
  onDeleteInvoice: (invoice: Invoice) => void;
  canAddInvoice: boolean;
  customers: Array<{
    id: string;
    customerId: string;
    name: string;
  }>;
}
export function Invoices({
  invoices,
  onAddInvoice,
  onEditInvoice,
  onDeleteInvoice,
  canAddInvoice,
  customers
}: InvoicesProps) {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [paidFilter, setPaidFilter] = useState<string>('all');
  const isReadOnly = !canAddInvoice;
  // Filter invoices
  const filteredInvoices = useMemo(() => {
    return invoices.filter(invoice => {
      const matchesSearch = invoice.supplier.toLowerCase().includes(searchQuery.toLowerCase()) || invoice.invoiceNumber.toLowerCase().includes(searchQuery.toLowerCase()) || invoice.customerName?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || invoice.category === categoryFilter;
      const matchesPaid = paidFilter === 'all' || paidFilter === 'paid' && invoice.paid || paidFilter === 'unpaid' && !invoice.paid;
      return matchesSearch && matchesCategory && matchesPaid;
    });
  }, [invoices, searchQuery, categoryFilter, paidFilter]);
  // Calculate stats
  const totalAmount = invoices.reduce((sum, inv) => sum + inv.amount, 0);
  const paidAmount = invoices.filter(inv => inv.paid).reduce((sum, inv) => sum + inv.amount, 0);
  const unpaidAmount = totalAmount - paidAmount;
  return <div className="space-y-6" data-id="element-1810">
      {/* Header */}
      <header className="flex items-center justify-between" data-id="element-1811">
        <div data-id="element-1812">
          <h1 className="text-4xl font-bold text-stone-900 mb-2" data-id="element-1813">Fakturaer</h1>
          <p className="text-stone-600" data-id="element-1814">
            {isReadOnly ? 'Se dine indkøbsfakturaer' : 'Administrer dine indkøbsfakturaer'}
          </p>
        </div>
        {canAddInvoice && <Button onClick={() => setIsAddModalOpen(true)} className="flex items-center gap-2" data-id="element-1815">
            <PlusIcon className="w-5 h-5" data-id="element-1816" />
            Tilføj faktura
          </Button>}
      </header>

      {/* Stats */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1817">
        <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm" data-id="element-1818">
          <div className="flex items-center gap-3 mb-2" data-id="element-1819">
            <div className="p-2 bg-blue-100 rounded-lg" data-id="element-1820">
              <ReceiptIcon className="w-5 h-5 text-blue-700" data-id="element-1821" />
            </div>
            <p className="text-sm font-medium text-stone-600" data-id="element-1822">Total beløb</p>
          </div>
          <p className="text-3xl font-bold text-stone-900" data-id="element-1823">
            {totalAmount.toLocaleString('da-DK')} kr
          </p>
          <p className="text-sm text-stone-500 mt-1" data-id="element-1824">
            {invoices.length} fakturaer
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm" data-id="element-1825">
          <div className="flex items-center gap-3 mb-2" data-id="element-1826">
            <div className="p-2 bg-emerald-100 rounded-lg" data-id="element-1827">
              <ReceiptIcon className="w-5 h-5 text-emerald-700" data-id="element-1828" />
            </div>
            <p className="text-sm font-medium text-stone-600" data-id="element-1829">Betalt</p>
          </div>
          <p className="text-3xl font-bold text-emerald-700" data-id="element-1830">
            {paidAmount.toLocaleString('da-DK')} kr
          </p>
          <p className="text-sm text-stone-500 mt-1" data-id="element-1831">
            {invoices.filter(inv => inv.paid).length} fakturaer
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm" data-id="element-1832">
          <div className="flex items-center gap-3 mb-2" data-id="element-1833">
            <div className="p-2 bg-red-100 rounded-lg" data-id="element-1834">
              <ReceiptIcon className="w-5 h-5 text-red-700" data-id="element-1835" />
            </div>
            <p className="text-sm font-medium text-stone-600" data-id="element-1836">Ubetalt</p>
          </div>
          <p className="text-3xl font-bold text-red-700" data-id="element-1837">
            {unpaidAmount.toLocaleString('da-DK')} kr
          </p>
          <p className="text-sm text-stone-500 mt-1" data-id="element-1838">
            {invoices.filter(inv => !inv.paid).length} fakturaer
          </p>
        </div>
      </motion.div>

      {/* Search and Filters */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm" data-id="element-1839">
        <div className="flex items-center gap-3 mb-4" data-id="element-1840">
          <FilterIcon className="w-5 h-5 text-stone-600" data-id="element-1841" />
          <h2 className="text-lg font-semibold text-stone-900" data-id="element-1842">
            Søg og filtrer
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1843">
          <div className="relative" data-id="element-1844">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" data-id="element-1845" />
            <input type="text" placeholder="Søg efter leverandør, faktura nr. eller kunde..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-all" data-id="element-1846" />
          </div>

          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-stone-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-all" data-id="element-1847">
            <option value="all" data-id="element-1848">Alle kategorier</option>
            <option value="Sikkerhed" data-id="element-1849">Sikkerhed</option>
            <option value="Værktøj" data-id="element-1850">Værktøj</option>
            <option value="Materialer" data-id="element-1851">Materialer</option>
            <option value="Andet" data-id="element-1852">Andet</option>
          </select>

          <select value={paidFilter} onChange={e => setPaidFilter(e.target.value)} className="px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-stone-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-all" data-id="element-1853">
            <option value="all" data-id="element-1854">Alle status</option>
            <option value="paid" data-id="element-1855">Betalt</option>
            <option value="unpaid" data-id="element-1856">Ubetalt</option>
          </select>
        </div>
      </motion.div>

      {/* Invoice List */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} data-id="element-1857">
        {filteredInvoices.length === 0 ? <div className="bg-white rounded-xl p-12 text-center border border-stone-100" data-id="element-1858">
            <ReceiptIcon className="w-12 h-12 text-stone-300 mx-auto mb-4" data-id="element-1859" />
            <h3 className="text-lg font-medium text-stone-900 mb-2" data-id="element-1860">
              {searchQuery || categoryFilter !== 'all' || paidFilter !== 'all' ? 'Ingen fakturaer matcher dine filtre' : 'Ingen fakturaer endnu'}
            </h3>
            <p className="text-stone-500 mb-6" data-id="element-1861">
              {searchQuery || categoryFilter !== 'all' || paidFilter !== 'all' ? 'Prøv at justere dine søgekriterier' : isReadOnly ? 'Der er ingen fakturaer i systemet' : 'Tilføj din første faktura for at komme i gang'}
            </p>
            {!searchQuery && categoryFilter === 'all' && paidFilter === 'all' && canAddInvoice && <Button onClick={() => setIsAddModalOpen(true)} data-id="element-1862">
                  Tilføj faktura
                </Button>}
          </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1863">
            {filteredInvoices.map((invoice, index) => <InvoiceCard key={invoice.id} invoice={invoice} index={index} onEdit={setEditingInvoice} onDelete={onDeleteInvoice} readOnly={isReadOnly} data-id="element-1864" />)}
          </div>}
      </motion.div>

      {canAddInvoice && <>
          <AddInvoiceModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onAdd={onAddInvoice} customers={customers} data-id="element-1865" />

          <EditInvoiceModal isOpen={!!editingInvoice} onClose={() => setEditingInvoice(null)} onSave={onEditInvoice} invoice={editingInvoice} customers={customers} data-id="element-1866" />
        </>}
    </div>;
}